import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Tag, AlertCircle } from 'lucide-react';
import API_URL from '../../config/api';
import CustomAlert from './shared/CustomAlert';

function CategoriasManager({ token, clientData }) {
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [planLimits, setPlanLimits] = useState(null);
  const [customAlert, setCustomAlert] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCategories();
    loadPlanLimits();
  }, []);

  const showAlert = (type, title, message) => setCustomAlert({ type, title, message });

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pos/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  const loadPlanLimits = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/plan-limits/${clientData.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlanLimits(response.data);
    } catch (error) {
      console.error('Error al cargar límites del plan:', error);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name || !newCategory.name.trim()) {
      showAlert('warning', 'Campo requerido', 'Por favor ingresa un nombre para la categoría.');
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/pos/categories`, newCategory, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadCategories();
      loadPlanLimits();
      closeModal();
      showAlert('success', 'Categoría creada', 'La categoría se agregó correctamente.');
    } catch (error) {
      console.error('Error al crear categoría:', error);
      if (error.response?.status === 403) {
        const d = error.response.data;
        showAlert('error', 'Límite alcanzado',
          `${d.error}\n\n${d.message}\n\nPlan: ${planLimits?.limits?.name || 'Básico'}\nCategorías: ${d.current}/${d.limit}\n\nActualiza tu plan para agregar más categorías.`
        );
      } else {
        showAlert('error', 'Error', error.response?.data?.error || 'No se pudo crear la categoría.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!newCategory.name || !newCategory.name.trim()) {
      showAlert('warning', 'Campo requerido', 'Por favor ingresa un nombre para la categoría.');
      return;
    }
    try {
      await axios.put(
        `${API_URL}/api/pos/categories/${editingCategory.id}`,
        newCategory,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadCategories();
      closeModal();
      showAlert('success', 'Categoría actualizada', 'Los cambios se guardaron correctamente.');
    } catch (error) {
      console.error('Error al actualizar categoría:', error);
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo actualizar la categoría.');
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/pos/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadCategories();
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error al eliminar categoría:', error);
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo eliminar la categoría.');
    }
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setNewCategory({ name: category.name });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setNewCategory({ name: '' });
  };

  const getPlanLimitInfo = () => {
    if (!planLimits) return null;
    const { limits } = planLimits;
    const count = categories.length;
    const max = limits.maxCategories;
    if (max === -1) return { text: `${count} categorías (Ilimitado)`, percentage: 0, color: 'text-green-600', bgColor: 'bg-green-100' };
    const percentage = (count / max) * 100;
    let color = 'text-green-600', bgColor = 'bg-green-100';
    if (percentage >= 90) { color = 'text-red-600'; bgColor = 'bg-red-100'; }
    else if (percentage >= 70) { color = 'text-yellow-600'; bgColor = 'bg-yellow-100'; }
    return { text: `${count}/${max} categorías`, percentage, color, bgColor };
  };

  const limitInfo = getPlanLimitInfo();

  return (
    <div className="p-6">
      {customAlert && <CustomAlert {...customAlert} onClose={() => setCustomAlert(null)} compact zIndex={50} />}

      {/* Confirm delete */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Eliminar categoría</h3>
            <p className="text-gray-600 text-sm mb-6">
              ¿Eliminar <strong>{confirmDelete.name}</strong>? Los productos de esta categoría quedarán sin categoría asignada.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button onClick={() => handleDeleteCategory(confirmDelete.id)} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestión de Categorías</h2>
          {planLimits && limitInfo && (
            <div className="flex items-center gap-2 mt-2">
              <span className={`text-sm font-semibold ${limitInfo.color}`}>{limitInfo.text}</span>
              {planLimits.limits.maxCategories !== -1 && (
                <div className="w-32">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`${limitInfo.bgColor} h-2 rounded-full`} style={{ width: `${Math.min(limitInfo.percentage, 100)}%` }} />
                  </div>
                </div>
              )}
              <span className={`text-xs px-2 py-1 rounded ${limitInfo.bgColor} ${limitInfo.color} font-semibold`}>
                Plan {planLimits.limits.name}
              </span>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nueva Categoría
        </button>
      </div>

      {planLimits && limitInfo && limitInfo.percentage >= 80 && planLimits.limits.maxCategories !== -1 && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-800">
            Cerca del límite: <strong>{planLimits.limits.maxCategories}</strong> categorías máx. en plan {planLimits.limits.name}.
          </p>
        </div>
      )}

      {/* Grid de categorías */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category) => (
          <div key={category.id} className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Tag className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{category.name}</h3>
                  <p className="text-xs text-gray-500">
                    {category.isDefault ? 'Categoría por defecto' : 'Categoría personalizada'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEditModal(category)} className="text-blue-600 hover:text-blue-900 p-1">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => setConfirmDelete(category)} className="text-red-600 hover:text-red-900 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="text-center py-12">
          <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay categorías registradas</p>
          <button onClick={() => setShowModal(true)} className="mt-4 text-blue-600 hover:underline">
            Crear primera categoría
          </button>
        </div>
      )}

      {/* Modal crear/editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4 text-gray-900">
              {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de la Categoría *</label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: Bebidas, Entradas, Postres"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !saving) editingCategory ? handleUpdateCategory() : handleCreateCategory();
                }}
              />
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={closeModal} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {saving ? 'Guardando...' : (editingCategory ? 'Actualizar' : 'Crear')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoriasManager;
